/**
 * The waiting state.
 *
 * `role="status"` with the label in the DOM rather than only an animation:
 * a spinning shape communicates nothing to a screen reader, and "loading"
 * with no subject communicates little to anyone.
 *
 * AND IT SAYS SO WHEN THE WAIT IS LONG. The backend runs on Cloud Functions
 * with no warm instances, so the first call after an idle period waits for a
 * container to start. Measured on the approvals endpoint: the actual work took
 * 0.3–0.8 seconds, while the requests that had to start an instance took 13
 * and 25 seconds — and the delay lands on the CORS preflight, before our code
 * runs at all, so there is nothing the server can send to say "hold on".
 *
 * A spinner that looks identical at second one and second twenty-five reads as
 * broken, which is exactly what was reported. After a few seconds this says
 * what is happening and roughly how long it may take, so waiting becomes a
 * decision rather than a guess.
 */

import { useEffect, useState } from 'react';
import styles from './Spinner.module.css';

/**
 * How long before the wait is worth remarking on.
 *
 * Long enough that a normal warm call (under a second) never trips it, short
 * enough to arrive before somebody concludes the page is stuck.
 */
export const SLOW_AFTER_MS = 4000;

export function Spinner({ label = 'Loading' }: { label?: string }) {
  const [slow, setSlow] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setSlow(true), SLOW_AFTER_MS);
    return () => clearTimeout(t);
  }, []);

  return (
    <div className={styles.wrap} role="status" aria-live="polite">
      <span className={styles.ring} aria-hidden="true" />
      <span className={styles.label}>{label}…</span>
      {slow && (
        <span className={styles.slow}>
          Still working. The server sleeps when it is not in use, so the first
          request after a quiet spell can take up to half a minute.
        </span>
      )}
    </div>
  );
}
